import React, { useEffect, useRef, useState } from "react";
import { MonitorPlay, LayoutGrid, SignalHigh, SignalLow } from "lucide-react";
import { useLang } from "@/lib/i18n";

/**
 * LivePlayer — Cloudflare Stream iframe with STABLE src across signed-JWT churn.
 *
 * WHY THIS COMPONENT IS memo'd AND USES A LOCKED src STATE
 * -------------------------------------------------------
 * `/api/live/{token}` mints a NEW short-lived signed playback JWT on every poll
 * (default TTL 90s). Naively binding `iframe.src` to `playback.iframe_url` would
 * rewrite the DOM `src` attribute every 4 seconds — a hard reload of the LL-HLS
 * player each time, causing intermittent stutter/freeze.
 *
 * Contract:
 *   - The iframe mounts ONCE per active broadcast and its `src` remains stable
 *     across every subsequent JWT-only refresh from the parent.
 *   - "Broadcast" is identified by `broadcastKey` (parent-supplied stable id like
 *     order_number + start_time). When `broadcastKey` genuinely changes, we
 *     switch to the new stream with the latest signed URL.
 *   - LIVE ↔ PAUSED transitions keep the iframe mounted — Cloudflare shows what
 *     OBS is currently sending at the source.
 *   - If the iframe ever fires `onError` (e.g., token expired mid-viewing), we
 *     do ONE controlled reload with the newest signed URL and lock again.
 */

// Compose the exact query string once at commit time. `muted` is captured at
// mount so runtime toggles of the visual mute button do NOT reload the iframe
// (the button is intentionally symbolic — Cloudflare's own player controls
// remain the source of truth for audio, matching existing behaviour).
function buildSrc(iframeUrl, muted) {
  return `${iframeUrl}?autoplay=true&muted=${muted}&primaryColor=%2300e5ff`;
}

function TopRightControls({ pseudoFs, nativeFsActive, toggleFullscreen, connectionStrength, canPlay }) {
  if (!canPlay) return null;
  const activeFs = pseudoFs || nativeFsActive;
  const btnBase = {
    background: "rgba(0,0,0,0.55)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    boxShadow: "0 4px 12px -4px rgba(0,0,0,0.5)",
  };
  // NOTE: A custom mute/volume button used to sit here (Emergent-authored,
  // symbolic). It was removed per product spec — audio control is now
  // owned exclusively by Cloudflare's own player UI inside the <iframe>.
  // The iframe still boots with `?muted=true` because Chrome/Safari require
  // muted-autoplay; the customer un-mutes via the native Cloudflare control.
  return (
    <div
      className="absolute z-20 flex items-center gap-1.5"
      style={{ top: "clamp(10px, 2.5%, 18px)", right: "clamp(10px, 2.5%, 18px)" }}
      data-testid="player-controls-topright"
    >
      <div
        className="w-9 h-9 md:w-10 md:h-10 rounded-lg border flex items-center justify-center"
        style={{
          ...btnBase,
          borderColor: connectionStrength === "high" ? "rgba(0,229,255,0.45)" : "rgba(255,255,255,0.14)",
        }}
        title="Connection"
        data-testid="player-connection"
      >
        {connectionStrength === "high"
          ? <SignalHigh size={16} strokeWidth={1.8} style={{ color: "var(--accent)" }} />
          : <SignalLow size={16} strokeWidth={1.8} className="text-white/60" />}
      </div>
      <button
        type="button"
        onClick={toggleFullscreen}
        title="Fullscreen"
        aria-label={activeFs ? "Exit fullscreen" : "Enter fullscreen"}
        data-testid="player-fullscreen"
        className="w-9 h-9 md:w-10 md:h-10 rounded-lg border flex items-center justify-center text-white/80 hover:text-white transition-colors"
        style={{ ...btnBase, borderColor: activeFs ? "rgba(0,229,255,0.6)" : "rgba(255,255,255,0.14)", color: activeFs ? "var(--accent)" : undefined }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(0,229,255,0.55)"; e.currentTarget.style.color = "var(--accent)"; }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = activeFs ? "rgba(0,229,255,0.6)" : "rgba(255,255,255,0.14)";
          e.currentTarget.style.color = activeFs ? "var(--accent)" : "";
        }}
      >
        <LayoutGrid size={16} strokeWidth={1.8} />
      </button>
    </div>
  );
}

function LivePlayerImpl({ playback, status, broadcastKey, revoked, refreshPlayback, onAutoReconnect, autoReconnectMs = 60000, overlaySlot = null }) {
  const { t } = useLang();
  const isLive = status === "LIVE";
  const isPaused = status === "PAUSED";
  const isTesting = status === "TESTING";
  const isWaiting = status === "WAITING";
  // Keep the player mounted during PAUSED — Cloudflare renders whatever OBS is
  // sending at the source, and we do not want to unmount/remount around a
  // customer-facing pause.
  const canPlay = (isLive || isPaused || isTesting) && !!playback?.iframe_url;

  const [lockedSrc, setLockedSrc] = useState(null);
  const [pseudoFs, setPseudoFs] = useState(false);
  const frameRef = useRef(null);
  const containerRef = useRef(null);
  const committedBroadcastRef = useRef(null);
  // Cloudflare autoplay policy requires the iframe to boot muted; the
  // customer un-mutes via Cloudflare's native player control inside the
  // iframe. This ref is a constant `true` — never toggled — so nothing here
  // ever rewrites the iframe src on audio state changes.
  const mutedAtMountRef = useRef(true);

  // ————— Container-level fullscreen —————
  //
  // Fullscreen must be applied to the OUTER wrapper (containerRef) so that
  // both the <iframe> AND the TestingVideoOverlay remain visible inside the
  // fullscreen element. If we fullscreened the iframe itself (as the previous
  // implementation did, and as Cloudflare's own in-iframe fullscreen button
  // does), any sibling overlay would disappear.
  //
  // - Prefers the standard `Element.requestFullscreen()` (works on modern
  //   Safari/iPadOS 16.4+, Chrome, Firefox, Edge).
  // - Falls back to WebKit-prefixed `webkitRequestFullscreen` (older Safari).
  // - If neither is available (iPhone Safari on iOS < 16.4 does not support
  //   element fullscreen; only HTMLVideoElement.webkitEnterFullscreen works,
  //   which would fullscreen just the iframe internals), applies a
  //   position:fixed pseudo-fullscreen using state — same effect visually,
  //   overlay stays inside the container, iframe DOM node unchanged.
  // - `fullscreenchange`/`webkitfullscreenchange` listeners keep pseudo state
  //   in sync so ESC or the browser's native exit works. Pseudo-fs also
  //   honours the Escape key for parity.
  //
  // `isFullscreen` is the single source of truth exposed to the top-right
  // toggle button — it flips to `true` whether native FS or the pseudo-FS
  // fallback is active, and back to `false` on ESC / native exit / API exit.
  const isFullscreen = pseudoFs; // updated below via fullscreenchange listener too
  const enterFullscreen = React.useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
    if (req) {
      const p = req.call(el);
      if (p && typeof p.catch === "function") {
        p.catch(() => setPseudoFs(true));
      }
      return;
    }
    setPseudoFs(true);
  }, []);
  const exitFullscreen = React.useCallback(() => {
    const nativeFs =
      document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement;
    if (nativeFs) {
      const exit = document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen;
      if (exit) {
        const p = exit.call(document);
        if (p && typeof p.catch === "function") p.catch(() => {});
      }
      return;
    }
    setPseudoFs(false);
  }, []);
  const [nativeFsActive, setNativeFsActive] = useState(false);
  const toggleFullscreen = React.useCallback(() => {
    if (pseudoFs || nativeFsActive) exitFullscreen();
    else enterFullscreen();
  }, [pseudoFs, nativeFsActive, enterFullscreen, exitFullscreen]);

  useEffect(() => {
    const sync = () => {
      const nativeFs =
        document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement;
      setNativeFsActive(nativeFs === containerRef.current);
    };
    sync();
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, []);

  useEffect(() => {
    if (!pseudoFs) return;
    const onKey = (e) => { if (e.key === "Escape") setPseudoFs(false); };
    document.addEventListener("keydown", onKey);
    // Prevent background scroll while in pseudo-fullscreen — restore on exit.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [pseudoFs]);

  // Always holds the freshest signed URL from props. Used by the error-recovery
  // listener so it can rebuild `src` even if the impl has already committed a
  // stale locked value.
  const latestUrlRef = useRef(null);
  useEffect(() => {
    latestUrlRef.current = playback?.iframe_url || null;
  }, [playback?.iframe_url]);

  useEffect(() => {
    if (!canPlay) return;
    const nextKey = broadcastKey || "default";
    // Case 1: brand-new broadcast (broadcastKey changed) — always commit fresh.
    if (committedBroadcastRef.current !== nextKey) {
      committedBroadcastRef.current = nextKey;
      setLockedSrc(buildSrc(playback.iframe_url, mutedAtMountRef.current));
      return;
    }
    // Case 2: same broadcast, no locked src yet (first LIVE frame after WAITING)
    // — commit once. JWT-only churn from subsequent polls is intentionally
    // ignored, so this effect will NOT re-fire (the deps that change are just
    // the iframe_url string, but the guard here refuses to overwrite once
    // committedBroadcastRef is set for this key).
    if (lockedSrc === null) {
      setLockedSrc(buildSrc(playback.iframe_url, mutedAtMountRef.current));
    }
    // NOTE: we intentionally include `playback?.iframe_url` in deps so we can
    // detect a broadcastKey→URL change race, but we DO NOT commit on url-only
    // change. React 18/19 batches the equal state set → no DOM src rewrite.
  }, [broadcastKey, canPlay, playback?.iframe_url, lockedSrc]);

  // Controlled recovery: native 'error' listener on the iframe element. Using a
  // native listener (instead of React's synthetic onError) is essential because
  // some browsers/JSDOM do not delegate iframe error events through React's
  // event system. The ref-based reader guarantees we always recover using the
  // freshest signed URL, not a stale closure.
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const onErr = () => {
      const fresh = latestUrlRef.current;
      if (fresh) setLockedSrc(buildSrc(fresh, mutedAtMountRef.current));
    };
    el.addEventListener("error", onErr);
    return () => el.removeEventListener("error", onErr);
  }, [lockedSrc]);

  // Safari-specific controlled reload on the PAUSED → LIVE transition.
  //
  // WebKit's HTMLMediaElement silently stalls the internal <video> inside
  // Cloudflare's iframe during a live-edge pause, and does NOT auto-resume
  // (Chrome and Firefox do). Manually refreshing Safari is the customer's
  // current workaround — this effect performs exactly ONE equivalent
  // controlled iframe reload for them, using the freshest signed URL from
  // `latestUrlRef.current`.
  //
  // Guarantees:
  //   - Fires ONLY on the PAUSED → LIVE edge (not on WAITING → LIVE — that is
  //     already handled by the broadcast-key/first-commit useEffect above).
  //   - Fires AT MOST once per RESUME — subsequent polls don't change `status`
  //     so the transition branch is skipped.
  //   - Sustained LIVE polling never enters this branch (`prev !== "PAUSED"`).
  //   - Uses `latestUrlRef.current`, so the reload always carries the newest
  //     signed JWT from the most recent poll (typically <4s old).
  const prevStatusRef = useRef(status);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;
    if (prev === "PAUSED" && status === "LIVE") {
      const fresh = latestUrlRef.current;
      if (fresh) setLockedSrc(buildSrc(fresh, mutedAtMountRef.current));
    }
  }, [status]);

  // ————— TEMPORARY: Periodic Cloudflare player auto-reconnect —————
  //
  // Cloudflare's LL-HLS <iframe> occasionally freezes mid-broadcast on
  // long-lived customer sessions. Until we address that at the source (OBS
  // / Cloudflare configuration), we do a controlled iframe-only reload
  // every `autoReconnectMs` (default 60 s) while the build is LIVE /
  // PAUSED / TESTING. This does NOT reload the page, does NOT unmount the
  // React tree, does NOT touch the chat WS or testing state — only the
  // `<iframe src>` is rewritten with the newest signed playback URL.
  //
  // Guarantees:
  //   - Exactly ONE interval alive at any time (StrictMode-safe via cleanup).
  //   - Stops when status leaves LIVE / PAUSED / TESTING, when the session
  //     is revoked, or when the component unmounts.
  //   - Before the reload, we (optionally) ask the parent to refetch a
  //     fresh signed URL via `refreshPlayback()`, so we never rebuild with
  //     a token that's about to expire.
  //   - The freshest signed URL is written through `latestUrlRef.current`
  //     by the effect at the top of this component — never a stale closure.
  //   - Signed tokens are never logged from the client; parent emits a
  //     name-only `PLAYER_AUTO_RECONNECT` beacon carrying no token.
  useEffect(() => {
    const shouldRun = !revoked && (status === "LIVE" || status === "PAUSED" || status === "TESTING");
    if (!shouldRun || !autoReconnectMs) return;
    const id = setInterval(async () => {
      // Preferred: ask parent for a freshly-minted signed URL. Parent's
      // fetchState() bumps `playback.iframe_url` prop, which our top-of-
      // component effect mirrors into `latestUrlRef.current`.
      if (typeof refreshPlayback === "function") {
        try { await refreshPlayback(); } catch { /* ignore — fall back to last polled URL */ }
      }
      const fresh = latestUrlRef.current;
      // Only rebuild if the iframe is actually mounted (canPlay=true). During
      // TESTING (no iframe) we still emit the beacon for observability but do
      // not touch DOM.
      if (fresh && frameRef.current) {
        setLockedSrc(buildSrc(fresh, mutedAtMountRef.current));
      }
      try { onAutoReconnect?.(); } catch { /* never let a beacon failure break playback */ }
    }, autoReconnectMs);
    return () => clearInterval(id);
  }, [status, revoked, autoReconnectMs, refreshPlayback, onAutoReconnect]);

  const connection = isLive ? "high" : "low";

  return (
    <div
      ref={containerRef}
      className={
        pseudoFs
          ? "fixed inset-0 z-[9999] w-screen h-screen bg-black flex items-center justify-center"
          : "relative w-full overflow-hidden rounded-2xl border"
      }
      style={
        pseudoFs
          ? { background: "#000" }
          : {
              aspectRatio: "16 / 9",
              borderColor: isLive
                ? "rgba(0,229,255,0.35)"
                : "rgba(255,255,255,0.08)",
              background: "#0a0a0d",
              boxShadow: isLive
                ? "0 0 0 1px rgba(0,229,255,0.15), 0 30px 80px -30px rgba(0,229,255,0.25), inset 0 0 40px rgba(0,229,255,0.04)"
                : "0 0 0 1px rgba(0,229,255,0.05), 0 30px 60px -30px rgba(0,229,255,0.08)",
            }
      }
      data-testid="live-player"
      data-fullscreen={pseudoFs ? "pseudo" : "off"}
    >
      {canPlay && lockedSrc ? (
        <>
          <iframe
            ref={frameRef}
            src={lockedSrc}
            className="w-full h-full"
            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
            allowFullScreen
            title="Live Build Stream"
            data-testid="live-player-iframe"
          />
          {overlaySlot}
          {isPaused && (
            <div
              className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center px-6"
              style={{
                background:
                  "radial-gradient(circle at 50% 45%, rgba(255,152,0,0.10), rgba(0,0,0,0) 55%), #0a0a0d",
              }}
              data-testid="live-player-paused-overlay"
            >
              <div
                className="w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center border relative"
                style={{
                  borderColor: "rgba(255,152,0,0.45)",
                  background: "rgba(255,152,0,0.08)",
                  color: "#ff9800",
                }}
              >
                <span
                  className="absolute inset-[-6px] rounded-full"
                  style={{
                    background: "#ff9800",
                    opacity: 0.18,
                    animation: "pausedPulse 1.6s ease-out infinite",
                  }}
                />
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="6" y="4" width="4" height="16" rx="1" />
                  <rect x="14" y="4" width="4" height="16" rx="1" />
                </svg>
              </div>
              <div
                className="mt-6 text-white text-[16px] leading-tight md:text-2xl font-semibold tracking-tight"
                data-testid="paused-overlay-headline"
              >
                BUILD PAUSED
              </div>
              <div
                className="mt-1 md:mt-2 text-white/55 text-[12px] font-normal md:text-[15px] md:font-normal leading-snug max-w-md"
                data-testid="paused-overlay-subtitle"
              >
                Your technician has temporarily paused the build. Playback will resume automatically.
              </div>
              <style>{`
                @keyframes pausedPulse {
                  0%   { transform: scale(0.7); opacity: 0.22; }
                  70%  { transform: scale(1.75); opacity: 0; }
                  100% { transform: scale(1.75); opacity: 0; }
                }
              `}</style>
            </div>
          )}
        </>
      ) : (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center text-center px-6"
          style={{
            background:
              "radial-gradient(circle at 50% 45%, rgba(0,229,255,0.08), rgba(0,0,0,0) 55%), #0a0a0d",
          }}
        >
          <div
            className="w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center border"
            style={{
              borderColor: "rgba(0,229,255,0.35)",
              background: "rgba(0,229,255,0.06)",
              color: "var(--accent)",
            }}
          >
            <MonitorPlay size={30} />
          </div>
          <div
            className="mt-6 text-white text-[16px] leading-tight md:text-2xl font-semibold tracking-tight"
            data-testid="player-status-headline"
          >
            {isWaiting ? t("player_waiting") : t("player_initializing")}
          </div>
          <div
            className="mt-1 md:mt-2 text-white/50 text-[12px] font-normal md:text-[15px] md:font-normal leading-snug max-w-md"
            data-testid="player-status-subtitle"
          >
            {isWaiting
              ? t("player_will_start")
              : playback?.provider === "cloudflare_pending"
              ? t("player_will_start")
              : t("player_preparing")}
          </div>
        </div>
      )}

      <TopRightControls
        pseudoFs={pseudoFs}
        nativeFsActive={nativeFsActive}
        toggleFullscreen={toggleFullscreen}
        connectionStrength={connection}
        canPlay={canPlay}
      />
    </div>
  );
}

/**
 * Note: We intentionally do NOT wrap this component in React.memo. The
 * stability guarantee comes from the ref + state guard inside the useEffect
 * below — `lockedSrc` state only ever updates for a genuine broadcast change
 * or a controlled error-recovery, so React reconciles the iframe with the
 * same `src` value and never touches the DOM attribute. Wrapping in memo
 * would freeze the closure over `playback`, making the onError handler
 * recover using a stale URL.
 */

export const LivePlayer = LivePlayerImpl;
